import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../app/components/Input';
import  Select  from '../app/components/Select';
import SelectResponsible from '../app/components/SelectResponsible';
import * as z from 'zod';
import { cepSchema, cnpjSchema, emailSchema, phoneSchema, formatCnpj, formatCep, formatPhone } from '../Validations/schemas';
import BaseForm from '../app/components/BaseForm';

const fields = [
  { id: 'responsavel', label: 'Responsável', type: 'multiselect', required: true },
  { id: 'cnpj', label: 'CNPJ', type: 'text', required: true },
  { id: 'regimeTributario', label: 'Regime Tributário', type: 'select' },
  { id: 'nomeFantasia', label: 'Nome Fantasia', type: 'text', required: true },
  { id: 'razaoSocial', label: 'Razão Social', type: 'text', required: true },
  { id: 'cidade', label: 'Cidade', type: 'text' },
  { id: 'uf', label: 'UF', type: 'select' },
  { id: 'endereco', label: 'Endereço', type: 'text' },
  { id: 'numero', label: 'Número', type: 'text' },
  { id: 'complemento', label: 'Complemento', type: 'text' },
  { id: 'cep', label: 'CEP', type: 'text' },
  { id: 'bairro', label: 'Bairro', type: 'text' },
  { id: 'nire', label: 'NIRE', type: 'text' },
  { id: 'fones', label: 'Fones', type: 'text' },
  { id: 'email', label: 'Email', type: 'text', required: true },
  { id: 'inscricaoMunicipal', label: 'Inscrição Municipal', type: 'text' },
  { id: 'inscricaoEstadual', label: 'Inscrição Estadual', type: 'text' },
  { id: 'dataInscricaoEstadual', label: 'Data Inscrição Estadual', type: 'date' },
  { type: 'inputComentario' },
];

const FormRegister = ({ isOpen, onClose, onSave, primaryColor = '#0052cc' }) => {
  const [formData, setFormData] = useState(() =>
    fields.reduce((acc, field) => ({ ...acc, [field.id]: field.type === 'multiselect' ? [] : '' }), {})
);
const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setFormData(fields.reduce((acc, field) => ({ ...acc, [field.id]: field.type === 'multiselect' ? [] : '' }), {}));
      setErrors({});
      }
    }, [isOpen]);

  const responsavelOptions = [
    { value: 'andre-anjos', label: 'André Anjos' },
    { value: 'elivelton-santos', label: 'Elivelton Santos' },
    { value: 'milena-uchoa', label: 'Milena Uchoa' },
    { value: 'paulo-pereira', label: 'Paulo Pereira' },
    { value: 'mrcelly-avelino', label: 'Marcelly Avelino' },
  ];

  const regimeTributarioOptions = [
    { value: 'simples-nacional', label: 'Simples Nacional' },
    { value: 'teste', label: '(Teste)' },
    { value: 'teste-b', label: 'teste b' },
  ];

  const ufOptions = 'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'
    .split(' ')
    .map((uf) => ({ value: uf, label: uf }));

 const handleChange = (field, value) => {
  let formattedValue = value;

  if (field === 'cnpj') {
    formattedValue = formatCnpj(value);
  } else if (field === 'cep') {
    formattedValue = formatCep(value);
  } else if (field === 'fones') {
    formattedValue = formatPhone(value);
  }

  setFormData((prev) => ({ ...prev, [field]: formattedValue }));

  if (typeof formattedValue === 'string' && formattedValue.trim() === '') {
    setErrors((prev) => ({ ...prev, [field]: '' }));
    return;
  }

  try {
    if (field === 'cnpj') {
      cnpjSchema.parse(formattedValue);
      setErrors((prev) => ({ ...prev, cnpj: '' }));
    } else if (field === 'cep') {
      cepSchema.parse(formattedValue);
      setErrors((prev) => ({ ...prev, cep: '' }));
    } else if (field === 'fones') {
      phoneSchema.parse(formattedValue);
      setErrors((prev) => ({ ...prev, fones: '' }));
    } else if (field === 'email') {
      emailSchema.parse(formattedValue);
      setErrors((prev) => ({ ...prev, email: '' }));
    }
  } catch (e) {
    if (e instanceof z.ZodError) {
      setErrors((prev) => ({ ...prev, [field]: e.errors[0].message }));
    }
  }
};



  const renderField = (field, index) => {
    const isFull = field.type === 'inputComentario' ? 'md:col-span-2' : '';
    const commonProps = {
      id: field.id,
      name: field.id,
      label: field.label,
      required: field.required,
    };

    let component;

    if (field.type === 'multiselect') {
      const selectedOptions = responsavelOptions.filter((opt) => formData[field.id].includes(opt.value));
      component = (
        <SelectResponsible
          {...commonProps}
          options={responsavelOptions}
          value={formData.responsavel}
          onChange={(selectedOptions) => handleChange('responsavel', selectedOptions || [])}
        />
      );
    } else if (field.type === 'select') {
      const options = field.id === 'regimeTributario' ? regimeTributarioOptions : field.id === 'uf' ? ufOptions : [];
      component = (
        <Select
          {...commonProps}
          options={options}
          value={formData[field.id]}
          onChange={(e) => handleChange(e.target.name, e.target.value)}
        />
      );
    } else if (field.type === 'inputComentario') {
      component = (
        <div className="relative w-full mb-5">
          <textarea
            id="comentario"
            name="comentario"
            placeholder=" "
            value={formData.comentario}
            onChange={(e) => handleChange("comentario", e.target.value)}
            className="peer block w-full appearance-none border border-gray-300 rounded-tl-2xl rounded-tr-2xl rounded-br-2xl p-3 min-h-[100px] focus:outline-none focus:ring-1 focus:ring-purple-500/25 focus:border-purple-700 transition-all"
          />
          <label
            htmlFor="comentario"
            className="absolute left-4 bg-white px-1 text-sm text-gray-500 transition-all duration-75 ease-in pointer-events-none peer-placeholder-shown:top-3 peer-focus:top-[-0.5rem] peer-focus:translate-y-0 peer-focus:text-purple-700"
          >
          Comentários
          </label>
        </div>
      )
    } else {
      component = (
        <div>
          <Input
            {...commonProps}
            value={formData[field.id] || ''}
            type={field.type === 'textarea' ? 'textarea' : field.type}
            rows={field.type === 'textarea' ? 4 : undefined}
            onChange={(e) => handleChange(field.id, e.target.value)}
            error={!!errors[field.id]}
          />
          {errors[field.id] && (
            <p className="text-red-500 text-sm mt-[-0.95rem]">{errors[field.id]}</p>
          )}
        </div>

      );
    }

    return (
      <motion.div
        key={field.id}
        className={`space-y-2 ${isFull}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 + index * 0.05 }}
      >
        {component}
      </motion.div>
    );
  };

  const renderFormSection = (sectionFields) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {sectionFields.map((field, index) => renderField(field, index))}
    </div>
  );

  const renderFormFields = () => {
    const sections = [
      { title: 'Dados do Cliente', slice: [0, 5] },
      { title: 'Endereço', slice: [5, 12] },
      { title: 'Contato', slice: [12, 15] },
      { title: 'Informações Fiscais', slice: [15, 18] },
      { title: 'Informações Adicionais', slice: [18] },
    ];

    return (
      <div className="max-h-[calc(80vh-120px)] overflow-y-auto px-1">
        {sections.map(({ title, slice }, i) => (
          <div key={i} className="mt-6 mb-4 pb-2">
            <h3 className="text-md font-medium text-gray-700 mb-8">{title}</h3>
            {renderFormSection(fields.slice(...slice))}
          </div>
        ))}
      </div>
    );
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <BaseForm
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmitForm}
      title="Novo Cliente"
      primaryColor={primaryColor}
      submitButtonText="Cadastrar"
      cancelButtonText="Cancelar"
      isValid={true}
      className='max-w-4xl'
    >
      {renderFormFields()}
    </BaseForm>
  );
};

export default FormRegister;